import { useId, type ReactNode } from 'react';

export interface ProgressRingProps {
  /** 0…1 */
  ratio: number;
  size?: number;
  stroke?: number;
  /** Habit color instead of the accent gradient. */
  solid?: boolean;
  children?: ReactNode;
  className?: string;
  label?: string;
}

export function ProgressRing({ ratio, size = 76, stroke = 8, solid, children, className, label }: ProgressRingProps) {
  const id = useId();
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, ratio));
  return (
    <div
      className={`ring${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--accent)" />
            <stop offset="1" stopColor="var(--accent-2)" />
          </linearGradient>
        </defs>
        <circle className="ring__track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" />
        <circle
          className="ring__value"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          fill="none"
          stroke={solid ? 'var(--hc)' : `url(#${id})`}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped)}
          opacity={clamped === 0 ? 0 : 1}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {children !== undefined && <div className="ring__label">{children}</div>}
    </div>
  );
}
